<template>
  <div>
    <div
      v-if="!loading && !professorRubricsNotFound"
      class="review-container container-fixed-height"
    >
      <v-row class="header" no-gutters>
        <v-col align-self="center" class="mx-6">
          <div v-if="changesMade" class="text-h6">Review Submission</div>
          <div v-else class="d-flex align-center">
            <p class="text-h6 mr-2">
              Evaluate Submission
            </p>

            <v-chip v-if="evaluationIsCompleted" color="success" label small>
              Completed
            </v-chip>
          </div>
          <span class="text-subtitle-2 font-weight-regular">
            Original Score: {{ review.marks ? review.marks : 'Not yet scored' }}
            <br />
            Reviewed By: {{ review.professor }}
            <span v-if="review.date_published">
              |
              {{ $moment(review.date_published).format('ll') }}
            </span>
          </span>
        </v-col>

        <v-col cols="auto">
          <submissions-list-menu
            :submission-id="submissionId"
            :submission-list="submissionList"
            @change="$emit('change-submission', $event)"
          />
        </v-col>
      </v-row>

      <v-divider />

      <div
        v-if="mountedReview.rubrics.length > 0"
        class="content assignment-container-scrollbars pt-6 pb-6 pb-md-0 px-6"
      >
        <p
          v-if="!evaluationIsCompleted"
          class="text--secondary text-body-2 mb-4"
        >
          <v-icon>mdi-information-outline</v-icon> Here is the original review.
          Edit rubrics if needed.
        </p>

        <review-rubric
          :disabled="evaluationIsCompleted || iqaReviewAlreadyExists"
          :rubrics="mountedReview.rubrics"
          :show-iqa-selected="false"
          :show-professor-selected="true"
          @mark="markRubric($event)"
        />

        <v-row no-gutters>
          <v-col class="12">
            <p>
              Comments for this submission
              <span v-if="!evaluationIsCompleted">
                (edit if needed)
              </span>
            </p>
            <base-text-area
              v-model="currentReview.comments"
              outlined
              :readonly="evaluationIsCompleted"
              rows="8"
            />
          </v-col>

          <v-col cols="12">
            <v-alert
              dense
              text
              type="error"
              :value="!changesMade && showNoChangesMessage"
            >
              Looks like no changes were made to the review. Please edit the
              review before submitting or accept the original review.
            </v-alert>
          </v-col>

          <v-col v-if="!evaluationIsCompleted" cols="12">
            <v-row>
              <v-spacer />
              <v-col cols="12" sm="auto">
                <confirmation-dialog
                  ref="acceptOriginalReviewDialog"
                  title="Accept professor's review?"
                  @confirm="acceptOriginalReview"
                >
                  <template v-slot:activator="{ attrs, on }">
                    <v-btn
                      v-bind="attrs"
                      class="rounded"
                      depressed
                      width="100%"
                      v-on="on"
                    >
                      Accept Original Review
                    </v-btn>
                  </template>

                  This will mark the professor's review as evaluated and will be
                  shown to the student.
                </confirmation-dialog>
              </v-col>
              <v-col cols="12" sm="auto">
                <confirmation-dialog
                  ref="markPlagiarisedDialog"
                  title="Mark Plagiarised?"
                  @confirm="markPlagiarised"
                >
                  <template v-slot:activator="{ attrs, on }">
                    <v-btn
                      v-bind="attrs"
                      class="rounded"
                      color="error"
                      depressed
                      width="100%"
                      v-on="on"
                    >
                      Mark Plagiarised
                    </v-btn>
                  </template>
                  <template v-slot:confirm="{ loading, onClick }">
                    <v-btn
                      color="primary"
                      depressed
                      :disabled="plagiarismRemarks.length === 0"
                      :loading="loading"
                      @click.native="onClick"
                    >
                      Confirm
                    </v-btn>
                  </template>

                  <template v-slot:default>
                    <p class="mb-1">
                      Please leave remarks for the submission
                    </p>
                    <base-text-area
                      v-model="plagiarismRemarks"
                      outlined
                      placeholder="Enter remarks for the student here."
                      rows="8"
                    />
                  </template>
                </confirmation-dialog>
              </v-col>
              <v-col cols="12" sm="auto">
                <v-btn
                  class="rounded"
                  color="primary"
                  depressed
                  width="100%"
                  @click="openCommentBox"
                >
                  Submit Changes
                </v-btn>
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="12">
            <v-expand-transition>
              <div
                v-show="showCommentsForProfessor"
                id="submission-container"
                class="mt-6"
              >
                <v-divider />
                <v-row class="mt-3" no-gutters>
                  <v-col
                    class="d-flex justify-space-between align-center"
                    cols="12"
                  >
                    <p class="text-h6 font-weight-bold">
                      Comments for the professor
                    </p>
                    <v-btn icon @click="showCommentsForProfessor = false">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-col>
                  <v-col cols="12">
                    <base-text-area
                      v-model="currentReview.evaluatorNotes"
                      outlined
                      placeholder="Please leave comments for the professor explaining the changes made in the review"
                      rows="8"
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-row>
                      <v-spacer />
                      <v-col cols="12" md="auto">
                        <confirmation-dialog
                          ref="submitReviewDialog"
                          title="Submit changes?"
                          @confirm="submitReview"
                        >
                          <template v-slot:activator="{ attrs, on }">
                            <v-btn
                              v-bind="attrs"
                              class="rounded"
                              color="primary"
                              depressed
                              :disabled="
                                currentReview.evaluatorNotes.length === 0
                              "
                              width="100%"
                              v-on="on"
                            >
                              Submit
                            </v-btn>
                          </template>

                          <template v-slot:default>
                            This will notify the professor that their review has
                            been changed. They can choose to either accept or
                            challenge your evaluation.
                          </template>
                        </confirmation-dialog>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </div>
            </v-expand-transition>
          </v-col>
        </v-row>
      </div>
    </div>
    <div v-else class="pa-5 review-container container-fixed-height">
      <div
        v-if="professorRubricsNotFound"
        class="d-flex justify-center align-center full-height"
      >
        <h3 v-if="professorRubricsNotFound">
          Your remarks could not be loaded.
        </h3>
        <h3 v-else>IQA remarks could not be loaded.</h3>
      </div>
      <skeleton-loading v-else :items="9" />
    </div>
  </div>
</template>

<script>
import ReviewRubric from '@/components/assignments/ReviewRubric'
import SkeletonLoading from '@/components/assignments/SkeletonLoading'
import SubmissionsListMenu from '@/components/assignments/SubmissionsListMenu'
import ConfirmationDialog from '@/components/_shared/ConfirmationDialog'

import {
  REVIEW_STATUS,
  REVIEW_EVALUATION_STATUS_CHOICES,
} from '@/utils/constants'

export default {
  name: 'EvaluateSubmission',

  components: {
    SkeletonLoading,
    ReviewRubric,
    SubmissionsListMenu,
    ConfirmationDialog,
  },

  props: {
    assignmentId: {
      type: String,
      required: true,
    },

    /**
     * Contains a list of all the submsissions for an assignment by a single user.
     */
    submissionList: {
      type: Array,
      required: true,
    },

    /**
     * Current submission's ID.
     */
    submissionId: {
      type: String,
      required: true,
    },

    /**
     * Review object of the currently mounted submission
     * If review.uuid is null, then the review does not exist.
     * If review.uuid is not null but review.status is "under_review", then again, then the review does not exist.
     * If review.status is one of ["accepted", "rejected", "plagiarised"], then review exists. The above mention cases will not happen in this component.
     */
    review: {
      type: Object,
      default: () => {},
    },

    iqaReviewAlreadyExists: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    /**
     * Boolean which is toggled incase the attempt to fetch rubrics fails
     */
    professorRubricsNotFound: false,

    /**
     * Object that stores the review that the user is currently marking
     * This will remain empty when there is no submission to review.
     */
    currentReview: {
      rubrics: [],
      evaluatorNotes: '',
      comments: '',
    },

    /**
     * When user is looking at a previous submission, the review of that submission will be stored here
     * By keeping the previous review data in a different state, we are able to preserve the markings
     * of the user (in currentReview)
     */
    previousReview: {
      rubrics: [],
      comments: '',
    },

    /**
     * Handles loading state for the entire component
     */
    loading: true,
    /**
     * Boolean which is toggled when the submit button is clicked
     */
    showCommentsForProfessor: false,

    showNoChangesMessage: false,

    plagiarismRemarks: '',
  }),

  computed: {
    /**
     * Check whether changes are made or not
     */
    changesMade() {
      let noChange = true
      for (const rubric of this.currentReview.rubrics) {
        for (const cell of rubric.cells) {
          if (
            cell.currentlySelected &&
            cell.currentlySelected !== cell.professorSelected
          ) {
            noChange = false
            break
          }
        }
        if (!noChange) break
      }
      return !noChange
    },

    mountedReview() {
      /**
       * Decide which review to show on the screen.
       * If the user is looking at an older submission, then we mount the review for that older submission
       * else we mount the 'editable' review which the user can edit
       */
      return this.iqaReviewAlreadyExists
        ? this.previousReview
        : this.currentReview
    },

    evaluationIsCompleted() {
      return (
        this.review.evaluationStatus ===
        REVIEW_EVALUATION_STATUS_CHOICES.ACCEPTED
      )
    },
  },

  watch: {
    submissionId() {
      this.getRubrics()
    },
    changesMade() {
      if (!this.changesMade) this.showCommentsForProfessor = false
    },
  },

  mounted() {
    this.getRubrics()
    this.currentReview.comments = this.review.comments
  },

  methods: {
    async getRubrics() {
      const isNotPlagiarised = this.review.status !== REVIEW_STATUS.PLAGIARISED

      try {
        /**
         * Determines the url to fetch professor Rubrics data from
         */
        const professorRes = isNotPlagiarised
          ? await this.$api.assignments.getMarkedRubrics({
              reviewId: this.review.uuid,
            })
          : // Yahan pe assignments wali hit hogi
            await this.$api.assignments.getRubrics({
              assignmentId: this.assignmentId,
            })

        /**
         * Sorting both lists on criterion so their order is identical.
         * Two objects can have same criteria, so, also sorting on task to achieve consistent ordering between both lists
         */
        const professorMarkings = this.sortData(
          this.sortData(professorRes.data.data, 'criteria'),
          'task'
        )

        if (professorMarkings.length === 0) this.professorRubricsNotFound = true

        const rubrics = professorMarkings.map(value => ({
          marked: isNotPlagiarised,
          id: value.id,
          criteria: value.criteria,
          task: value.task,
          cells: this.sortData(
            value.all_comments.map(cell => ({
              uuid: cell.uuid,
              scale: cell.scale,
              text: cell.comments,
              professorSelected: isNotPlagiarised
                ? value.marked.uuid === cell.uuid
                : false,
              currentlySelected: false,
            })),
            'scale'
          ),
        }))

        if (this.iqaReviewAlreadyExists) {
          this.previousReview = {
            ...this.previousReview,
            rubrics,
          }
        } else {
          this.currentReview = {
            ...this.currentReview,
            rubrics,
          }
        }
      } catch (e) {
        console.error(e)

        // Uncomment these
        this.professorRubricsNotFound = true
        this.iqaRubricsNotFound = true
      } finally {
        this.loading = false
      }
    },

    sortData(list, key) {
      return list.sort((a, b) => (a[key] > b[key] ? 1 : -1))
    },

    openCommentBox() {
      if (this.changesMade) {
        this.showCommentsForProfessor = true
        const container = document.querySelector('#submission-container')
        setTimeout(() => {
          container.scrollIntoView({ behavior: 'smooth' })
        }, 400)
      } else {
        this.showNoChangesMessage = true
      }
    },

    async acceptOriginalReview() {
      const data = {
        status: REVIEW_EVALUATION_STATUS_CHOICES.ACCEPTED,
      }

      const dialog = this.$refs.acceptOriginalReviewDialog
      dialog.confirmBtnLoading = true

      try {
        await this.$api.assignments.submitReview({
          reviewId: this.review.uuid,
          data,
        })
        this.$emit('submit', {
          evaluationStatus: data.status,
        })
        this.showCommentsForProfessor = false
        dialog.close()
      } catch (e) {
        console.error(e)
        if (e.response?.data.errors?.message)
          this.$toast.error(e.response.data.errors.message)
      } finally {
        dialog.confirmBtnLoading = false
      }
    },

    async markPlagiarised() {
      const data = {
        comments: this.plagiarismRemarks,
        status: REVIEW_EVALUATION_STATUS_CHOICES.CHANGES_REQUESTED,
        review_status: REVIEW_STATUS.PLAGIARISED,
      }

      const dialog = this.$refs.markPlagiarisedDialog
      dialog.confirmBtnLoading = true

      try {
        const res = await this.$api.assignments.submitReview({
          reviewId: this.review.uuid,
          data,
        })
        this.$emit('submit', {
          evaluationStatus: data.status,
          iqaReview: {
            uuid: res.data.data.iqa_review.uuid,
            marks: 0,
            evaluatorNotes: data.evaluator_notes,
            comments: data.comments,
            status: data.review_status,
          },
        })
        dialog.close()
      } catch (e) {
        console.error(e)
        if (e.response?.data.errors?.message)
          this.$toast.error(e.response.data.errors.message)
      } finally {
        dialog.confirmBtnLoading = false
      }
    },

    async submitReview() {
      if (
        this.currentReview.evaluatorNotes === '' ||
        this.currentReview.comments === ''
      ) {
        this.$toast.error(
          'Please make sure you leave comments for the student and the professor'
        )
        return
      }

      const dialog = this.$refs.submitReviewDialog
      dialog.confirmBtnLoading = true

      // For all the cells that IQA may have left unchanged, we pick up the cell marked by the professor
      const markedRubricCellIds = this.currentReview.rubrics.map(value => {
        const cell =
          value.cells.find(value => value.currentlySelected) ||
          value.cells.find(value => value.professorSelected)

        return cell.uuid
      })

      const data = {
        status: REVIEW_EVALUATION_STATUS_CHOICES.CHANGES_REQUESTED,
        evaluator_notes: this.currentReview.evaluatorNotes,
        comments: this.currentReview.comments,
        rubric_cells: markedRubricCellIds,
      }

      try {
        const res = await this.$api.assignments.submitReview({
          reviewId: this.review.uuid,
          data,
        })
        this.$emit('submit', {
          evaluationStatus: data.status,
          iqaReview: {
            uuid: res.data.data.iqa_review.uuid,
            marks: res.data.data.iqa_review.marks.toFixed(2),
            evaluatorNotes: data.evaluator_notes,
            comments: data.comments,
            status: res.data.data.iqa_review.status,
          },
        })
        dialog.close()
        this.$toast.success('Your review has been submitted')
      } catch (e) {
        console.error(e)
        if (e.response?.data.errors?.message)
          this.$toast.error(e.response.data.errors.message)
      }

      dialog.confirmBtnLoading = false
    },

    markRubric({ scale, criteria }) {
      const selectedCriteria = this.currentReview.rubrics.find(
        currentCriteria => currentCriteria.id === criteria
      )

      const newCells = selectedCriteria.cells.map(cell => {
        cell.currentlySelected = cell.scale === scale
        return cell
      })
      selectedCriteria.cells = newCells
      selectedCriteria.marked = true

      this.currentReview.rubrics = this.currentReview.rubrics.map(obj => {
        if (selectedCriteria.id === obj.id) {
          obj.cells = newCells
          obj.marked = true
        }
        return obj
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.header {
  min-height: 100px;
}

.full-height {
  height: 100%;
}

.no-changes-container {
  margin-top: 1rem;
  border-radius: 5px;
  background-color: lighten(#cc2d26, 35%);
  color: #cc2d26;
  i {
    color: #cc2d26;
  }
}

.review-container {
  background: white;
  border-radius: 5px;
}

@media #{map-get($display-breakpoints, 'md-and-up')} {
  .content {
    height: calc(100% - 120px);
    overflow-y: auto;
    overflow-x: hidden;
  }
}
</style>
